// Which copy of a picture is the real one.
//
// A picture can reach a deck three ways: uploaded (a base64 data URL written
// into the deck), linked (an address the deck stores and the browser fetches),
// or referenced by a path the app itself serves. Trouble starts when two of
// those describe the same picture — an upload that was later replaced by a link
// leaves its bytes behind, and the export used to prefer those bytes.
//
// The rule, in one place:
//
//   A durable external URL is the source of truth. It is never copied into the
//   deck or into an export as base64. Bytes are only ever embedded for a
//   picture that has no durable address to point at.
//
// "Durable" means an absolute https address: it resolves the same from the
// editor, from an exported file on a memory stick, and from an LMS. It
// deliberately excludes:
//
//   data:   — already the bytes
//   blob:   — dies with the browser tab that made it
//   http:   — blocked as mixed content the moment a deck is opened over https,
//             so a copy of those bytes is the only thing that will render
//   /paths  — served by this app, and meaningless once the file is moved

const DATA_URL = /^data:/i;
const DURABLE_URL = /^(https:\/\/|\/\/)/i;

export const isDataUrl = (value) => typeof value === 'string' && DATA_URL.test(value.trim());

/** An address an exported file can point at instead of carrying the bytes. */
export const isDurableExternalUrl = (value) => typeof value === 'string' && DURABLE_URL.test(value.trim());

/**
 * Should this source be fetched and written into the export as base64?
 *
 * Only when there is nothing durable to point at. This is the single question
 * every inlining site in the export asks.
 */
export const shouldInlineForExport = (value) => Boolean(value)
  && typeof value === 'string'
  && !isDataUrl(value)
  && !isDurableExternalUrl(value);

/**
 * The one source to keep for an asset, given every representation of it.
 *
 * Metadata is the caller's business — this decides bytes versus address only,
 * and never invents a source that was not passed in.
 *
 * @param {Array<string>} candidates in order of preference
 * @returns {string} the durable address if any candidate has one, else the
 *          first usable candidate (a data URL, a path, whatever exists)
 */
export const normalizeMediaSource = (candidates = []) => {
  const usable = candidates.filter((value) => typeof value === 'string' && value.trim());
  return usable.find(isDurableExternalUrl) || usable[0] || '';
};

// --- export diagnostics ----------------------------------------------------

const R2_HOSTS = /(^|\/\/)([a-z0-9-]+\.)*(lpoassets\.ca|r2\.dev|r2\.cloudflarestorage\.com)/i;
const VIDEO_HOSTS = /(youtube\.com|youtu\.be|vimeo\.com|panopto|player\.vimeo\.com|video\.disney\.com)/i;

const contextAround = (html, index, span = 90) => html
  .slice(Math.max(0, index - span), index)
  .replace(/\s+/g, ' ')
  .slice(-span);

/**
 * What an exported file actually contains, so a surprise is visible instead of
 * merely large. Every embedded picture is reported with the markup around it,
 * which is what identifies the block it came from.
 */
export const auditExportedHtml = (html = '') => {
  const embedded = [];
  const pattern = /data:image\/[a-z0-9.+-]+;base64,/gi;
  let match = pattern.exec(html);
  while (match) {
    // How far the base64 runs, so the report can say what it costs.
    const tail = html.slice(match.index + match[0].length);
    const end = tail.search(/["')\s]/);
    const bytes = match[0].length + (end === -1 ? tail.length : end);
    embedded.push({
      kind: match[0].replace(/^data:/, '').replace(/;base64,$/, ''),
      bytes,
      context: contextAround(html, match.index)
    });
    match = pattern.exec(html);
  }

  const urls = Array.from(html.matchAll(/(?:src|href)="(https:\/\/[^"]+)"|url\((?:&quot;|["']?)(https:\/\/[^"')]+)/gi))
    .map((entry) => entry[1] || entry[2])
    .filter(Boolean);

  const imageUrls = urls.filter((url) => /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|$)/i.test(url) || R2_HOSTS.test(url));

  return {
    externalImages: imageUrls.length,
    r2Images: imageUrls.filter((url) => R2_HOSTS.test(url)).length,
    flickrImages: imageUrls.filter((url) => /staticflickr\.com|flickr\.com/i.test(url)).length,
    externalVideoLinks: urls.filter((url) => VIDEO_HOSTS.test(url)).length,
    base64Images: embedded.length,
    base64Bytes: embedded.reduce((total, item) => total + item.bytes, 0),
    embedded
  };
};

/** The audit as the line-per-fact block a developer wants in the console. */
export const formatAssetAudit = (audit, { orphansRemoved = 0 } = {}) => {
  const lines = [
    'HTML Asset Audit',
    '----------------',
    `External images: ${audit.externalImages}`,
    `R2 images: ${audit.r2Images}`,
    `External Flickr images: ${audit.flickrImages}`,
    `External video links: ${audit.externalVideoLinks}`,
    `Base64 images: ${audit.base64Images}${audit.base64Images ? ` (${(audit.base64Bytes / 1024).toFixed(0)} KB)` : ''}`,
    `Orphaned assets removed: ${orphansRemoved}`
  ];
  if (audit.base64Images) {
    lines.push('', 'Base64 remaining:');
    audit.embedded.forEach((item) => {
      lines.push(`- ${item.kind}, ${(item.bytes / 1024).toFixed(0)} KB — …${item.context}`);
    });
  }
  return lines.join('\n');
};
